import { useEffect } from 'react';
import { useBasket } from '~/features/basket';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import {
    _matchCouponsFromCartWithProducts,
    _getCouponsFromOrder,
} from '~/shared/hooks/useEvents/useGTMEvents/helper';
import { useFrame } from '~/shared/utils';
import { withErrorBoundary } from '~/shared/utils/errorBoundary';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { Container, LoaderSpinner } from './styled';
import { useRouter } from 'next/router';
import { useTranslation } from '~/shared/utils/translation';

const InStoreFinishOrderComponent = () => {
    const basket = useBasket();
    const { push } = useRouter();
    const customer = useCustomer();
    const { products } = useProducts();
    const { data: frame } = useFrame();
    const { addPaymentInfoEvent } = useEvents(frame);
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const { translate } = useTranslation();

    useEffect(() => {
        handleCreateOrder();
    }, []);

    const handleCreateOrder = async () => {
        try {
            if (
                !basket.billingAddress?.address1 ||
                (!basket.billingAddress.postalCode && customer.addresses?.length)
            ) {
                const address = customer.addresses?.[0];

                await basket.setBillingAddress(
                    {
                        ...basket.billingAddress,
                        address1: address?.address1,
                        postalCode: address?.postalCode,
                        city: address?.city,
                    },
                    { useAsShipping: false },
                );
            }

            const order = await basket.createOrder();

            if (products?.length) {
                const couponCodes = _matchCouponsFromCartWithProducts(products, basket.couponItems);
                const orderCoupons = _getCouponsFromOrder(basket);
                addPaymentInfoEvent(
                    products.map((item, index) => {
                        item.coupon = couponCodes?.appliedCoupons[item.id]?.couponCode;
                        return _transformProductToDataLayer(item, item.quantity, index);
                    }),
                    String(basket.orderTotal),
                    orderCoupons,
                    order?.paymentInstruments?.[0].paymentInstrumentId ?? '',
                );
            }

            if (order && frame.settings?.pageReferences?.receiptPage?.url) {
                // using window.location to make sure that all state has been reset, since we have an issue where its not
                window.location.href =
                    frame.settings?.pageReferences?.receiptPage?.url + '?orderNo=' + order.orderNo;
            }
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (e?.message?.toUpperCase() === 'ERROR') {
                trackError(translate('message.generalErrorMessage'));
            } else {
                trackError(e);
            }
            await push({
                pathname: frame.settings?.pageReferences.basketPage?.url,
            });
        }
    };

    return (
        <Container>
            <LoaderSpinner />
        </Container>
    );
};

export const InStoreFinishOrder = withErrorBoundary(InStoreFinishOrderComponent);
